import PropTypes from "prop-types";
import { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router";
import { DEFAULT_BANNER } from "../../config";
import SignType from "../../domain/signType";
import { useAutoScroll, useJsonLd } from "../../hooks";
import useSuppressChat from "../../hooks/useSuppressChat.js";
import { captureWithShop } from "../../services/posthogCapture.js";
import { SHOP_VIEWED } from "../../services/posthogEvents.js";
import { getParameter, sendSessionEvent } from "../../util";
import CustomUploadBanner from "./components/CustomUploadBanner.jsx";
import CustomUploadDrawer from "./components/CustomUploadDrawer.jsx";
import Events from "./components/Events/Events.jsx";
import Hero from "./components/Hero.jsx";
import ProductSection from "./components/ProductSection.jsx";
import RelatedShops from "./components/RelatedShops.jsx";
import { Alert } from "../../themeV2";
import ShopEmpty from "./components/ShopEmpty.jsx";
import ShopHero from "./components/ShopHero.jsx";
import s from "./components/ShopPage.module.css";
import Toolbar from "./components/Toolbar.jsx";
import { ShopFiltersProvider, useProductSelection } from "./hooks/index.js";
import { findNextEvent, productAvailability } from "./utils.js";
import CanonicalUrl from "../../components/CanonicalUrl";

const VIDEO_AVATAR_CDN =
  "https://d3qdxs9s8vjwda.cloudfront.net/influencerVideo";

function MetaData({ influencer, products }) {
  const personSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Person",
      name: influencer.name,
      image: influencer.avatar_url || undefined,
      url: influencer.seo?.canonical_url || undefined,
    }),
    [influencer.name, influencer.avatar_url, influencer.seo?.canonical_url],
  );

  const itemListSchema = useMemo(() => {
    if (!products?.length) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${influencer.name} Autographed Products`,
      numberOfItems: products.length,
      itemListElement: products.map((product, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Product",
          name: product.name,
          image: product.image_url || undefined,
          url: influencer.seo?.canonical_url
            ? `${influencer.seo.canonical_url}/${product.product_id}`
            : undefined,
          offers: {
            "@type": "Offer",
            price: (
              (product.price + (product.product_bump || 0)) /
              100
            ).toFixed(2),
            priceCurrency: "USD",
            availability: productAvailability(product),
          },
        },
      })),
    };
  }, [products, influencer.name, influencer.seo?.canonical_url]);

  useJsonLd(personSchema);
  useJsonLd(itemListSchema);

  return null;
}

MetaData.propTypes = {
  influencer: PropTypes.shape({
    name: PropTypes.string.isRequired,
    avatar_url: PropTypes.string,
    seo: PropTypes.shape({
      canonical_url: PropTypes.string,
    }),
  }),
  products: PropTypes.array,
};

export default function ShopPage({
  route,
  influencer,
  shopProducts,
  isLoading,
  isPreview,
}) {
  const { enableAutoScroll, disableAutoScroll } = useAutoScroll();
  useSuppressChat(true);

  useEffect(() => {
    disableAutoScroll();
    return () => enableAutoScroll();
  }, [disableAutoScroll, enableAutoScroll]);

  const isCollection = Boolean(influencer?.group_shop);

  const selection = useProductSelection(shopProducts.customImageProducts, {
    route,
  });

  const nextEventDate = useMemo(() => {
    const allEvents = [...shopProducts.events, ...shopProducts.nonLiveEvents];
    const next = findNextEvent(allEvents);
    return next ? new Date(next.next_event_time) : null;
  }, [shopProducts.events, shopProducts.nonLiveEvents]);

  const hasLivestreamProducts = useMemo(
    () =>
      (shopProducts.allProducts ?? []).some((p) => {
        const types = p._variants
          ? p._variants.map((v) => v.signed_type)
          : [p.signed_type];
        return types.some(SignType.isLivestreamSigning);
      }),
    [shopProducts.allProducts],
  );

  useEffect(() => {
    if (!influencer?.influencer_id) {
      return;
    }

    captureWithShop(
      SHOP_VIEWED,
      {
        shop_id: influencer.influencer_id,
        shop_route: influencer.route,
        shop_name: influencer.name,
        tracking_code:
          getParameter("stc") || localStorage?.getItem?.("stc") || null,
      },
      influencer.influencer_id,
    );

    sendSessionEvent({
      type: "shop-view",
      payload: { id: influencer.influencer_id, name: influencer.name },
    });
  }, [influencer]);

  const { productId } = useParams();
  const isShopRoute = !productId;

  return (
    <>
      {isPreview && (
        <Alert severity="info" className={s.previewBanner}>
          You are previewing your storefront. Customers cannot see this view
          while your shop is closed.
        </Alert>
      )}

      {influencer && isShopRoute && (
        <Helmet>
          <title>
            {influencer.seo?.title || `Streamily | ${influencer.name}`}
          </title>

          <meta
            name="description"
            content={
              influencer.seo?.description ||
              `Autographed Collectibles Signed by ${influencer.name}`
            }
          />

          {Boolean(influencer.meta_tag) && (
            <meta name="keywords" content={influencer.meta_tag.famousFor} />
          )}

          {/* Open Graph */}
          <meta property="og:type" content="website" />
          <meta
            property="og:title"
            content={influencer.seo?.title || `Streamily | ${influencer.name}`}
          />
          <meta
            property="og:description"
            content={
              influencer.seo?.description ||
              `Autographed Collectibles Signed by ${influencer.name}`
            }
          />
          {influencer.seo?.social_thumbnail && (
            <meta
              property="og:image"
              content={influencer.seo.social_thumbnail}
            />
          )}
          {influencer.seo?.canonical_url && (
            <meta property="og:url" content={influencer.seo.canonical_url} />
          )}
          <meta property="og:site_name" content="Streamily" />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta
            name="twitter:title"
            content={influencer.seo?.title || `Streamily | ${influencer.name}`}
          />
          <meta
            name="twitter:description"
            content={
              influencer.seo?.description ||
              `Autographed Collectibles Signed by ${influencer.name}`
            }
          />
          {influencer.seo?.social_thumbnail && (
            <meta
              name="twitter:image"
              content={influencer.seo.social_thumbnail}
            />
          )}
        </Helmet>
      )}

      {influencer && isShopRoute && (
        <MetaData influencer={influencer} products={shopProducts.allProducts} />
      )}
      {influencer?.seo?.canonical_url && isShopRoute && (
        <CanonicalUrl href={influencer.seo.canonical_url} />
      )}

      <ShopFiltersProvider
        allProducts={shopProducts.allProducts}
        formatOptions={shopProducts.formatOptions}
        signingTypes={shopProducts.signingTypes}
        characterList={shopProducts.characterList}
        vaList={shopProducts.vaList}
        charactersMap={shopProducts.charactersMap}
      >
        {isCollection ? (
          <>
            <Hero
              avatarUrl={influencer?.avatar_url}
              bannerUrl={influencer?.banner_url ?? DEFAULT_BANNER}
              caption={influencer?.caption}
              hasLivestreamProducts={hasLivestreamProducts}
              influencerId={influencer?.influencer_id}
              isLoading={isLoading}
              name={influencer?.name}
              priceMax={shopProducts.stats?.priceMax}
              priceMin={shopProducts.stats?.priceMin}
              productCount={shopProducts.stats?.productCount}
              vaCount={shopProducts.stats?.vaCount}
            />
          </>
        ) : (
          <ShopHero
            avatarUrl={influencer?.avatar_url}
            bannerUrl={influencer?.banner_url ?? DEFAULT_BANNER}
            caption={influencer?.caption}
            charityName={influencer?.charity_name}
            hasLivestreamProducts={hasLivestreamProducts}
            hasVideoAvatar={Boolean(influencer?.has_video_avatar)}
            influencerId={influencer?.influencer_id}
            isLoading={isLoading}
            name={influencer?.name}
            socials={shopProducts.socials}
            videoAvatarUrl={
              influencer?.video_avatar_url ||
              (influencer?.has_video_avatar
                ? `${VIDEO_AVATAR_CDN}/${influencer?.influencer_id}.mov`
                : null)
            }
            productCount={shopProducts.allProducts?.length ?? 0}
            nextEventDate={nextEventDate}
          />
        )}

        <div className={s.topSection} />

        {(shopProducts.events?.length > 0 ||
          shopProducts.nonLiveEvents?.length > 0) && (
          <div className={s.section}>
            <Events
              liveEvents={shopProducts.events}
              nonLiveEvents={shopProducts.nonLiveEvents}
            />
          </div>
        )}

        {!isLoading && influencer && shopProducts.allProducts?.length === 0 ? (
          <ShopEmpty
            influencerName={influencer.name}
            influencerId={influencer.influencer_id}
            route={route}
          />
        ) : (
          <>
            <Toolbar isCollection={isCollection} isLoading={isLoading} />

            <div id="shop-products" className={s.main}>
              <CustomUploadBanner
                onGetStarted={selection.handleCustomGetStarted}
                products={shopProducts.customImageProducts}
              />
              <ProductSection
                isLoading={isLoading}
                onProductClick={selection.handleProductClick}
              />
              <RelatedShops
                excludeTag={influencer?.product_tag_string}
                isLoading={isLoading}
                productTags={influencer?.productTags}
                influencerId={influencer?.influencer_id}
              />
            </div>

            <CustomUploadDrawer
              onClose={() => selection.setCustomDrawerOpen(false)}
              onSelect={selection.handleCustomSelect}
              open={selection.customDrawerOpen}
              products={shopProducts.customImageProducts}
            />
          </>
        )}
      </ShopFiltersProvider>
    </>
  );
}

ShopPage.propTypes = {
  route: PropTypes.string.isRequired,
  influencer: PropTypes.object,
  shopProducts: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  isPreview: PropTypes.bool,
};
